import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { sendPasswordResetEmail } from "@/app/auth/forgot-password/forgot-password.slice";

const formSchema = z.object({
  email: z.string({ required_error: "Email is required" }).email("Invalid email address"),
});

export function ForgotPasswordForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, success, message } = useAppSelector((state) => state.forgotPassword);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: { email: string }) {
    await dispatch(sendPasswordResetEmail(values)).unwrap();
  }

  const formDefinition: FormFieldDef[] = [
    {
      type: "email",
      name: "email",
      label: "Email",
      placeholder: "Enter your email",
      required: true,
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit,
    formDefinition,
    buttonText: loading ? "Sending..." : "Send Reset Link",
  };

  if (success) {
    return (
      <>
        <div className="flex flex-col items-start gap-2 text-center mb-6">
          <h1 className="text-2xl font-bold">Check your email</h1>
          <p className="text-balance text-sm text-muted-foreground">
            {message || "We've sent you a password reset link. Please check your inbox."}
          </p>
        </div>
        <div className="text-center text-sm mt-4">
          <Link to="/login" className="underline underline-offset-4">
            Back to login
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Forgot your password?</h1>
        <p className="text-balance text-sm text-muted-foreground">
          Enter your email address and we'll send you a link to reset your password
        </p>
      </div>

      <FormCreator {...formCreatorProps} />

      {/* TODO: Error formatting. SYN-61 */}
      {error && <p className="text-sm text-destructive text-center mt-4">{error}</p>}

      <div className="text-center text-sm mt-4">
        Remember your password?{" "}
        <Link to="/login" className="underline underline-offset-4">
          Back to login
        </Link>
      </div>
    </>
  );
}

